import React, { useState, useEffect, useCallback } from 'react';
import { 
  ChatBubbleLeftRightIcon,
  UserCircleIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  XMarkIcon,
  ArrowPathIcon,
  BellIcon,
  TrashIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import api, { getImageUrl } from '../../services/api';
import ChatPopup from '../../components/ChatPopup';
import { adminService } from '../../services/admin';

interface Conversation {
  id: number;
  name: string;
  email: string;
  profile_image: string | null;
  status: string;
  unread_count: number;
  last_message: string | null;
  last_message_time: string | null;
}

const Chat: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userResults, setUserResults] = useState<Conversation[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<Conversation | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadConversations();
    // Refresh conversations every 30 seconds
    const interval = setInterval(loadConversations, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Auto-open first conversation with unread messages
    if (!hasAutoOpened && conversations.length > 0 && !selectedUserId) {
      const firstUnread = conversations.find(conv => conv.unread_count > 0);
      if (firstUnread) {
        handleSelectConversation(firstUnread.id);
        setHasAutoOpened(true);
      }
    }
  }, [conversations, hasAutoOpened, selectedUserId]);

  const loadConversations = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/messages/conversations');
      setConversations(response.data.conversations);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadConversations();
    setIsRefreshing(false);
  };

  const openNewChatModal = async () => {
    setShowNewChatModal(true);
    await searchUsers('');
  };

  const searchUsers = async (term: string) => {
    try {
      setIsLoadingUsers(true);
      const resp = await adminService.getUsers(1, 20, '', term);
      const users = (resp.users || []).map((u: any) => ({
        id: u.id,
        name: u.name,
        email: u.email,
        profile_image: u.profile_image || null,
        status: u.status || 'approved',
        unread_count: 0,
        last_message: null,
        last_message_time: null,
      })) as Conversation[];
      setUserResults(users);
    } catch (e) {
      console.error('Error loading users for new chat:', e);
      setUserResults([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const debouncedSearch = useCallback((term: string) => {
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Set new timeout for 300ms delay
    const timeout = setTimeout(() => {
      searchUsers(term);
    }, 300);
    
    setSearchTimeout(timeout);
  }, [searchTimeout]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const handleSelectConversation = (userId: number) => {
    setSelectedUserId(userId);
    setIsChatOpen(true);
  };

  const openDeleteConversation = (conversation: Conversation) => {
    setConversationToDelete(conversation);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteConversation = async () => {
    if (!conversationToDelete) return;
    try {
      setDeleting(true);
      await adminService.deleteConversation(conversationToDelete.id);
      setShowDeleteConfirm(false);
      setConversationToDelete(null);
      if (selectedUserId === conversationToDelete.id) {
        setIsChatOpen(false);
        setSelectedUserId(null);
      }
      await loadConversations();
    } catch (error) {
      console.error('Error deleting conversation:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseChat = () => {
    setIsChatOpen(false);
    setSelectedUserId(null);
    // Reload conversations to update unread counts
    loadConversations();
  };

  const formatTime = (dateString: string | null) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 168) { // 7 days
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort conversations to show unread first
  const sortedConversations = [...filteredConversations].sort((a, b) => {
    // First sort by unread count (unread messages first)
    if (a.unread_count > 0 && b.unread_count === 0) return -1;
    if (a.unread_count === 0 && b.unread_count > 0) return 1;
    
    // Then sort by last message time
    const timeA = a.last_message_time ? new Date(a.last_message_time).getTime() : 0;
    const timeB = b.last_message_time ? new Date(b.last_message_time).getTime() : 0;
    return timeB - timeA;
  });

  const totalUnread = conversations.reduce((sum, conv) => sum + conv.unread_count, 0);

  return (
    <div className="px-4 sm:px-0">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">ການສົນທະນາ</h1>
            <p className="mt-2 text-gray-600">ສົນທະນາກັບນັກຮຽນແລະຜູ້ໃຊ້ອື່ນໆ</p>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-4">
            {totalUnread > 0 && (
              <div className="flex items-center px-3 py-1 rounded-full bg-red-100 text-red-800">
                <BellIcon className="h-5 w-5 mr-2" />
                <span className="font-medium">{totalUnread} ຂໍ້ຄວາມທີ່ຍັງບໍ່ໄດ້ອ່ານ</span>
              </div>
            )}
            <button
              onClick={openNewChatModal}
              className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              title="Start new chat"
            >
              <UserPlusIcon className="h-5 w-5 mr-1" /> ເລີ່ມສົນທະນາ
            </button>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              title="Refresh conversations"
            >
              <ArrowPathIcon className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {/* Search Bar */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <input
              type="text"
              placeholder="ຄົ້ນຫາການສົນທະນາ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64 rounded-md border border-gray-300 shadow-sm py-2 pl-10 pr-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>
        </div>

        {/* Conversations List */}
        <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <p className="mt-2 text-sm text-gray-500">ກຳລັງໂຫຼດການສົນທະນາ...</p>
            </div>
          ) : sortedConversations.length === 0 ? (
            <div className="p-8 text-center">
              <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">ບໍ່ມີການສົນທະນາ</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm ? 'ບໍ່ພົບການສົນທະນາທີ່ກົງກັບການຄົ້ນຫາຂອງທ່ານ.' : 'ຍັງບໍ່ມີຂໍ້ຄວາມ.'}
              </p>
            </div>
          ) : (
            sortedConversations.map((conversation) => (
              <div
                key={conversation.id}
                className={`
                  p-4 hover:bg-gray-50 transition-colors duration-150 cursor-pointer
                  ${selectedUserId === conversation.id ? 'bg-indigo-50' : ''}
                `}
              >
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    {conversation.profile_image ? (
                      <img 
                        src={getImageUrl(conversation.profile_image)} 
                        alt={conversation.name}
                        className="h-10 w-10 rounded-full"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                        <UserCircleIcon className="h-6 w-6 text-gray-500" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0" onClick={() => handleSelectConversation(conversation.id)}>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900 truncate">
                          {conversation.name}
                      </p>
                          <p className="text-xs text-gray-500">
                            {formatTime(conversation.last_message_time)}
                          </p>
                      </div>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-sm text-gray-500 truncate">
                        {conversation.last_message || 'ຍັງບໍ່ມີຂໍ້ຄວາມ'}
                      </p>
                      {conversation.unread_count > 0 && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          {conversation.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); openDeleteConversation(conversation); }}
                      className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                      title="ລຶບການສົນທະນາ"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat Popup */}
      {isChatOpen && selectedUserId && (
        <ChatPopup
          isOpen={isChatOpen}
          selectedUserId={selectedUserId}
          onClose={handleCloseChat}
        />
      )}

      {showNewChatModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="relative w-full max-w-md mx-auto">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="px-4 pt-4 pb-2 sm:p-6 border-b">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-bold text-gray-900">ເລືອກຜູ້ໃຊ້ເພື່ອເລີ່ມສົນທະນາ</h3>
                  <button
                    type="button"
                    onClick={() => setShowNewChatModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                    aria-label="Close"
                    title="Close"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <div className="mt-3">
                  <div className="relative">
                    <input
                      type="text"
                      value={userSearchTerm}
                      onChange={(e) => { 
                        const value = e.target.value;
                        setUserSearchTerm(value);
                        debouncedSearch(value);
                      }}
                      placeholder="ຄົ້ນຫາຜູ້ໃຊ້..."
                      className="w-full rounded-md border border-gray-300 shadow-sm py-2 pl-10 pr-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  </div>
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {isLoadingUsers ? (
                  <div className="p-4 text-center text-gray-500">ກຳລັງໂຫຼດ...</div>
                ) : userResults.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">ບໍ່ພົບຜູ້ໃຊ້</div>
                ) : (
                  userResults.map((u) => (
                    <div key={`newchat-${u.id}`} className="p-3 flex items-center justify-between hover:bg-gray-50">
                      <div className="flex items-center space-x-3 min-w-0">
                        <div className="flex-shrink-0">
                          {u.profile_image ? (
                            <img src={getImageUrl(u.profile_image)} alt={u.name} className="h-8 w-8 rounded-full" />
                          ) : (
                            <UserCircleIcon className="h-8 w-8 text-gray-400" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{u.name}</div>
                          <div className="text-xs text-gray-500 truncate">{u.email}</div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedUserId(u.id);
                          setIsChatOpen(true);
                          setShowNewChatModal(false);
                        }}
                        className="px-3 py-1.5 bg-indigo-600 text-white text-xs rounded-md hover:bg-indigo-700"
                      >
                        ເລີ່ມສົນທະນາ
                      </button>
                    </div>
                  ))
                )}
              </div>
              <div className="px-4 py-3 bg-gray-50 text-right">
                <button
                  type="button"
                  onClick={() => setShowNewChatModal(false)}
                  className="inline-flex justify-center rounded-md border border-gray-300 shadow px-4 py-2 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  ຍົກເລີກ
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && conversationToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="relative w-full max-w-xs sm:max-w-md mx-auto">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="px-4 pt-4 pb-2 sm:p-6">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                    <TrashIcon className="h-5 w-5 text-red-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-gray-900 mb-1">ລຶບການສົນທະນາ</h3>
                    <p className="text-xs text-gray-700">
                      ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບການສົນທະນາກັບ <span className="font-semibold">{conversationToDelete.name}</span>?
                    </p>
                    <p className="text-xs text-red-600 mt-1">⚠️ ການກະທຳນີ້ບໍ່ສາມາດຍົກເລີກໄດ້.</p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 flex flex-col sm:flex-row-reverse gap-2">
                <button
                  type="button"
                  onClick={confirmDeleteConversation}
                  disabled={deleting}
                  className="w-full inline-flex justify-center rounded-lg border border-transparent shadow px-4 py-2 bg-red-600 text-xs font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleting ? 'ກຳລັງລຶບ...' : 'ລຶບການສົນທະນາ'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowDeleteConfirm(false); setConversationToDelete(null); }}
                  className="w-full inline-flex justify-center rounded-lg border border-gray-300 shadow px-4 py-2 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  ຍົກເລີກ
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chat;